import { useEffect, useRef, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import moment from "moment-timezone";
import { ChatbotEmbedProps, ChatbotAPI, ChatContext } from "./types";
import { getExperiementalChatbotEnabled } from "./utils";
import { selectCallerAccountId, selectIsKPIChatFeatureEnabled } from "../../store/app-config/AppConfigSelectors";
import { selectCurrentStoreId } from "../../store/store-list/StoreListSelectors";
import { selectUserLocale } from "../../store/user/UserSelectors";
import { selectCurrentCatalogId } from "../../store/catalog/CatalogSelectors";
import { selectCurrentCategory } from "../../store/category/CategorySelectors";
import { selectPerformanceReportStoreId } from "store/performance-report/PerformanceReportSelectors";
import { useHasEntitlement } from "../../hooks/useEntitlement";
import { getToken } from "../../services/UserService";

const CHATBOT_REQUEST_ACCESS_TOKEN = "SM_CHATBOT_REQUEST_ACCESS_TOKEN";
const CHATBOT_ACCESS_TOKEN = "SM_CHATBOT_ACCESS_TOKEN";

declare global {
  interface Window {
    SmartMerchandiserChatbot?: ChatbotAPI;
    SM_CHATBOT_API_URL?: string;
    setExperiementalChatbotEnabled?: (enabled: boolean) => void;
  }
}

/**
 * Adds a trailing slash to the given string if it doesn't already have one.
 * @param x - The string to add a trailing slash to.
 * @returns The string with a trailing slash.
 */
const withTrailingSlash = (x: string) => (x.endsWith("/") ? x : `${x}/`);

const experiementalChatbotEnabled = getExperiementalChatbotEnabled();

window.setExperiementalChatbotEnabled = (enabled: boolean) => {
  sessionStorage.setItem("experiementalChatbotEnabled", enabled.toString());
  window.location.reload();
};

export function ChatbotEmbed({
  apiUrl,
  position = "bottom-right",
  primaryColor = "rgb(241, 130, 33)",
  buttonText = "",
}: ChatbotEmbedProps) {
  const scriptLoaded = useRef(false);
  const [isChatbotReady, setIsChatbotReady] = useState(false);
  const location = useLocation();
  const history = useHistory();

  // Get context values from Redux store
  const accountId = useSelector(selectCallerAccountId);
  const catalogStoreId = useSelector(selectCurrentStoreId);
  const performanceStoreId = useSelector(selectPerformanceReportStoreId);
  const userLocale = useSelector(selectUserLocale);
  const catalogId = useSelector(selectCurrentCatalogId);
  const categoryId = useSelector(selectCurrentCategory);
  const hasAiEntitlement = useHasEntitlement("ai");
  const isKPIChatFeatureEnabled = useSelector(selectIsKPIChatFeatureEnabled);

  const storeId = location.pathname.includes("/performance")
    ? performanceStoreId
    : catalogStoreId;

  // Get chatbot URL from environment variable
  const finalChatbotUrl =
    (experiementalChatbotEnabled
      ? process.env.REACT_APP_CHATBOT_URL_EXPERIMENTAL
      : process.env.REACT_APP_CHATBOT_URL) ?? "";

  // Create context object with useMemo to prevent unnecessary re-renders
  const context: ChatContext = useMemo(
    () => ({
      accountId: accountId || undefined,
      storeId: storeId || undefined,
      timezone: moment.tz.guess(),
      userLocalePreference: userLocale || undefined,
      catalogId: catalogId || undefined,
      categoryId: categoryId || undefined,
      page: location.pathname,
      hasAiEntitlement,
      isKPIChatFeatureEnabled,
    }),
    [accountId, storeId, userLocale, catalogId, categoryId, location.pathname, hasAiEntitlement, isKPIChatFeatureEnabled],
  );

  useEffect(() => {
    // Only load the script once
    if (scriptLoaded.current) return;

    const embedScriptUrl = `${withTrailingSlash(finalChatbotUrl)}embed.js`;

    const loadEmbedScript = async () => {
      try {
        // Load the embed script from the chatbot URL
        const script = document.createElement("script");
        script.src = embedScriptUrl;
        script.async = true;

        script.onload = () => {
          // Initialize the chatbot once the script is loaded
          if (window.SmartMerchandiserChatbot) {
            window.SmartMerchandiserChatbot.init({
              chatbotUrl: finalChatbotUrl,
              apiUrl,
              position,
              primaryColor,
              buttonText,
              context,
              autoOpenPages: ["/performance/"],
              containerSelector: "#chatbot-container",
            });
            setIsChatbotReady(true);
          }
        };

        script.onerror = () => {
          console.warn(
            "Failed to load chatbot embed script. Falling back to iframe approach.",
          );
        };

        document.head.appendChild(script);
        scriptLoaded.current = true;
      } catch (error) {
        console.error("Error loading chatbot embed script:", error);
      }
    };

    loadEmbedScript();

    // Cleanup function
    return () => {
      // Remove the script if component unmounts
      const existingScript = document.querySelector(
        `script[src="${embedScriptUrl}"]`,
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [finalChatbotUrl, apiUrl, position, primaryColor, buttonText, context]);

  // Update context when values change
  useEffect(() => {
    if (
      window.SmartMerchandiserChatbot &&
      window.SmartMerchandiserChatbot.setContext
    ) {
      window.SmartMerchandiserChatbot.setContext(context);
    }
  }, [context]);

  useEffect(() => {
    let allowedOrigin: string | null = null;
    try {
      allowedOrigin = finalChatbotUrl ? new URL(finalChatbotUrl).origin : null;
    } catch {
      allowedOrigin = null;
    }
    if (!allowedOrigin) return;

    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type !== CHATBOT_REQUEST_ACCESS_TOKEN) return;
      if (event.origin !== allowedOrigin) return;
      const source = event.source as Window | null;
      if (!source || typeof event.data.requestId !== "string") return;

      let accessToken: string | null = null;
      try {
        const token = await getToken();
        accessToken = token ?? null;
      } catch {
        accessToken = null;
      }

      source.postMessage(
        {
          type: CHATBOT_ACCESS_TOKEN,
          requestId: event.data.requestId,
          accessToken,
        },
        event.origin,
      );
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [finalChatbotUrl]);

  // Track route changes and call checkRoute to trigger auto-open logic
  useEffect(() => {
    // Only set up listener if chatbot is ready
    if (!isChatbotReady || !window.SmartMerchandiserChatbot) {
      return;
    }

    // Listen for route changes
    const unlisten = history.listen(() => {
      // Call checkRoute to trigger checkAndAutoOpen in embed.js
      if (window.SmartMerchandiserChatbot?.checkRoute) {
        // Small delay to ensure route is fully updated
        setTimeout(() => {
          window.SmartMerchandiserChatbot?.checkRoute();
        }, 100);
      }
    });

    // Also check on initial mount/route change
    if (window.SmartMerchandiserChatbot?.checkRoute) {
      // Small delay to ensure route is fully updated
      setTimeout(() => {
        window.SmartMerchandiserChatbot?.checkRoute();
      }, 100);
    }

    // Cleanup listener on unmount
    return () => {
      unlisten();
    };
  }, [history, isChatbotReady]);

  // This component doesn't render anything - the embed script handles the UI
  return null;
}
